import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Monitor, Tablet, Smartphone, Eye, Undo2, Redo2,
  Trash2, Copy, GripVertical, Plus, ChevronDown, Save, Loader2, Rocket,
  LayoutGrid, Type, ImageIcon, Megaphone, MousePointerClick, PanelBottom,
  Zap, Shield, Sparkles, Code, Database, Layers, ChevronRight, Check,
  FileText, MessageSquare, DollarSign, Calendar,
  HelpCircle, BarChart3, Grid3X3, PlayCircle, Minus, Images, Settings, Globe,
} from 'lucide-react';
import BlockRenderer from '../components/sites/blocks/BlockRenderer';
import { BLOCK_REGISTRY, BLOCK_CATEGORIES, getBlockDefaults } from '../components/sites/blocks/registry';
import AIBuilderPanel from '../components/sites/AIBuilderPanel';
import ABTestPanel from '../components/sites/ABTestPanel';
import CustomDomainPanel from '../components/sites/CustomDomainPanel';
import FunnelStepBar from '../components/sites/FunnelStepBar';
import FunnelSettingsPanel from '../components/sites/FunnelSettingsPanel';
import GlobalStylesPanel from '../components/sites/GlobalStylesPanel';
import FigmaImportModal from '../components/sites/FigmaImportModal';
import URLImportModal from '../components/sites/URLImportModal';
import RawCodeEditor from '../components/sites/RawCodeEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

const CATEGORY_ICONS = {
  layout: LayoutGrid,
  content: Type,
  marketing: Megaphone,
  crm: Database,
  advanced: Code,
};

const BLOCK_ICON_MAP = {
  Monitor: Monitor,
  Type: Type,
  ImageIcon: ImageIcon,
  LayoutGrid: LayoutGrid,
  MousePointerClick: MousePointerClick,
  PanelBottom: PanelBottom,
  FileText: FileText,
  MessageSquare: MessageSquare,
  DollarSign: DollarSign,
  Calendar: Calendar,
  HelpCircle: HelpCircle,
  BarChart3: BarChart3,
  Grid3X3: Grid3X3,
  PlayCircle: PlayCircle,
  Minus: Minus,
  Images: Images,
};

const DEVICE_WIDTHS = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

// Batch 3 — starter scaffold for a brand-new raw_code page.
const STARTER_RAW_CODE = {
  html: '<section class="hero">\n  <h1>Your raw-code page</h1>\n  <p>Edit the HTML, CSS and JS tabs. Everything ships as-is on publish.</p>\n</section>',
  css: '.hero {\n  max-width: 720px;\n  margin: 0 auto;\n  padding: 80px 24px;\n  font-family: Manrope, sans-serif;\n  text-align: center;\n}',
  js: '// Runs as-is on the published page\nconsole.log("raw-code page loaded");',
};

// ─── Block Palette Item (draggable) ────────────────────────────────────────────

function BlockPaletteItem({ blockType, definition, onDragStateChange, onClickAdd }) {
  const Icon = BLOCK_ICON_MAP[definition.icon] || LayoutGrid;

  const handleDragStart = (e) => {
    e.dataTransfer.setData('application/x-block-type', blockType);
    e.dataTransfer.effectAllowed = 'copy';
    onDragStateChange?.(true);
  };

  const handleDragEnd = () => {
    onDragStateChange?.(false);
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={() => onClickAdd?.(blockType)}
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-stone-200 bg-white hover:bg-stone-50 hover:border-stone-300 cursor-grab active:cursor-grabbing transition-colors"
    >
      <div className="w-7 h-7 rounded-md bg-stone-100 flex items-center justify-center flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-stone-600" />
      </div>
      <span className="text-xs font-medium text-stone-700">{definition.label}</span>
      <Plus className="w-3 h-3 text-stone-400 ml-auto opacity-0 group-hover:opacity-100" />
    </div>
  );
}

// ─── Layers Panel Item ─────────────────────────────────────────────────────────

function LayerItem({ block, isSelected, onSelect, onDelete, index, onDragStart, onDragOver, onDrop }) {
  const definition = BLOCK_REGISTRY[block.block_type];
  const Icon = BLOCK_ICON_MAP[definition?.icon] || LayoutGrid;
  const preview = block.props?.headline || block.props?.content?.replace(/<[^>]*>/g, '') || '';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, index)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, index)}
      onClick={() => onSelect(block.block_id)}
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer group transition-colors ${
        isSelected
          ? 'bg-blue-50 border border-blue-200'
          : 'hover:bg-stone-50 border border-transparent'
      }`}
    >
      <GripVertical className="w-3 h-3 text-stone-300 cursor-grab flex-shrink-0" />
      <Icon className="w-3.5 h-3.5 text-stone-500 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-stone-700 truncate">{definition?.label || block.block_type}</p>
        {preview && (
          <p className="text-[10px] text-stone-400 truncate">{preview.slice(0, 40)}</p>
        )}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(block.block_id); }}
        className="opacity-0 group-hover:opacity-100 p-1 text-stone-400 hover:text-red-500 transition-opacity"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ─── Drop Zone (between blocks on canvas) ──────────────────────────────────────

function DropZone({ index, onDrop, isDragging }) {
  const [isOver, setIsOver] = useState(false);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsOver(true); }}
      onDragLeave={() => setIsOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const blockType = e.dataTransfer.getData('application/x-block-type');
        const layerIdx = e.dataTransfer.getData('application/x-layer-index');
        if (blockType) {
          onDrop(blockType, index);
        } else if (layerIdx !== '') {
          // reorder from layers panel drag (not used in canvas)
        }
      }}
      className={`transition-all rounded ${
        isOver
          ? 'h-16 bg-blue-100 border-2 border-dashed border-blue-400 flex items-center justify-center mx-4'
          : isDragging
          ? 'h-8 bg-blue-50/50 border border-dashed border-blue-200 mx-4 flex items-center justify-center'
          : 'h-1'
      }`}
    >
      {isOver && <span className="text-xs text-blue-500 font-medium">Drop here</span>}
      {!isOver && isDragging && <span className="text-[10px] text-blue-300">Drop block here</span>}
    </div>
  );
}

// ─── Properties Panel ──────────────────────────────────────────────────────────

function BlockPropertiesPanel({ block, onUpdate, onDelete, onDuplicate }) {
  if (!block) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-stone-400 px-6">
        <MousePointerClick className="w-8 h-8 mb-3 text-stone-300" />
        <p className="text-sm font-medium text-stone-500">Select a block</p>
        <p className="text-xs text-stone-400 mt-1 text-center">Click on a block in the canvas to edit its properties</p>
      </div>
    );
  }

  const definition = BLOCK_REGISTRY[block.block_type];
  const Icon = BLOCK_ICON_MAP[definition?.icon] || LayoutGrid;
  const props = block.props || {};

  const updateProp = (key, value) => {
    onUpdate(block.block_id, { ...props, [key]: value });
  };

  const renderEditor = () => {
    switch (block.block_type) {
      case 'hero':
        return (
          <div className="space-y-4">
            {/* Variant selector */}
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button
                    key={v}
                    onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v
                        ? 'bg-stone-900 text-white border-stone-900'
                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}
                  >
                    {v.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <textarea value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)}
                className={INPUT} rows={2} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <textarea value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)}
                className={INPUT} rows={2} />
            </div>
            <div>
              <label className={LABEL}>CTA Text</label>
              <input value={props.cta_text || ''} onChange={(e) => updateProp('cta_text', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>CTA URL</label>
              <input value={props.cta_url || ''} onChange={(e) => updateProp('cta_url', e.target.value)}
                className={INPUT} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
            </div>
            <div>
              <label className={LABEL}>Alignment</label>
              <div className="flex gap-1">
                {['left', 'center', 'right'].map((a) => (
                  <button key={a} onClick={() => updateProp('alignment', a)}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors capitalize ${
                      props.alignment === a
                        ? 'bg-stone-900 text-white border-stone-900'
                        : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Min Height</label>
              <select value={props.min_height || 'auto'} onChange={(e) => updateProp('min_height', e.target.value)}
                className={INPUT}>
                <option value="auto">Auto</option>
                <option value="50vh">50vh</option>
                <option value="75vh">75vh</option>
                <option value="100vh">Full Screen</option>
              </select>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Content</label>
              <textarea value={props.content?.replace(/<[^>]*>/g, '') || ''}
                onChange={(e) => updateProp('content', `<p>${e.target.value}</p>`)}
                className={INPUT} rows={8} placeholder="Type your content..." />
              <p className="text-[10px] text-stone-400 mt-1">HTML tags are supported</p>
            </div>
            <div>
              <label className={LABEL}>Max Width</label>
              <select value={props.max_width || 'prose'} onChange={(e) => updateProp('max_width', e.target.value)}
                className={INPUT}>
                <option value="prose">Prose (65ch)</option>
                <option value="2xl">2XL (672px)</option>
                <option value="4xl">4XL (896px)</option>
                <option value="full">Full Width</option>
              </select>
            </div>
          </div>
        );

      case 'html':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>HTML Content</label>
              <textarea
                value={props.html_content || ''}
                onChange={(e) => updateProp('html_content', e.target.value)}
                className="w-full px-3 py-2 text-xs font-mono bg-stone-900 text-green-400 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-600"
                rows={14}
                placeholder="<div>Paste your HTML here...</div>"
                spellCheck={false}
              />
              <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                <span>⚠</span> Scripts and event handlers are sanitized for security.
              </p>
            </div>
            <div>
              <label className={LABEL}>Custom CSS <span className="text-stone-400 font-normal">(optional)</span></label>
              <textarea
                value={props.custom_css || ''}
                onChange={(e) => updateProp('custom_css', e.target.value)}
                className="w-full px-3 py-2 text-xs font-mono bg-stone-900 text-green-400 border border-stone-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-600"
                rows={6}
                placeholder=".my-class { color: #333; }"
                spellCheck={false}
              />
            </div>
            <div>
              <label className={LABEL}>Max Width</label>
              <select value={props.max_width || 'full'} onChange={(e) => updateProp('max_width', e.target.value)}
                className={INPUT}>
                <option value="full">Full Width</option>
                <option value="contained">Contained (1100px)</option>
                <option value="narrow">Narrow (800px)</option>
              </select>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Image URL</label>
              <div className="flex gap-2">
                <input value={props.image_url || ''} onChange={(e) => updateProp('image_url', e.target.value)}
                  className={`${INPUT} flex-1`} placeholder="https://..." />
                <Button variant="outline" size="sm" className="flex-shrink-0 text-xs">Browse</Button>
              </div>
            </div>
            <div>
              <label className={LABEL}>Alt Text</label>
              <input value={props.alt_text || ''} onChange={(e) => updateProp('alt_text', e.target.value)}
                className={INPUT} placeholder="Describe the image..." />
            </div>
            <div>
              <label className={LABEL}>Caption</label>
              <input value={props.caption || ''} onChange={(e) => updateProp('caption', e.target.value)}
                className={INPUT} placeholder="Optional caption..." />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.rounded !== false}
                onChange={(e) => updateProp('rounded', e.target.checked)}
                className="rounded border-stone-300" />
              Rounded corners
            </label>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Features</label>
              <div className="space-y-2">
                {(props.items || []).map((item, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Feature {i + 1}</span>
                      <button
                        onClick={() => {
                          const items = [...(props.items || [])];
                          items.splice(i, 1);
                          updateProp('items', items);
                        }}
                        className="text-stone-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <input
                      value={item.icon || ''}
                      onChange={(e) => {
                        const items = [...(props.items || [])];
                        items[i] = { ...items[i], icon: e.target.value };
                        updateProp('items', items);
                      }}
                      className={INPUT} placeholder="Icon name (e.g. Zap, Shield)" />
                    <input
                      value={item.title || ''}
                      onChange={(e) => {
                        const items = [...(props.items || [])];
                        items[i] = { ...items[i], title: e.target.value };
                        updateProp('items', items);
                      }}
                      className={INPUT} placeholder="Feature title" />
                    <textarea
                      value={item.description || ''}
                      onChange={(e) => {
                        const items = [...(props.items || [])];
                        items[i] = { ...items[i], description: e.target.value };
                        updateProp('items', items);
                      }}
                      className={INPUT} rows={2} placeholder="Feature description" />
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const items = [...(props.items || []), { icon: 'Zap', title: 'New Feature', description: 'Description' }];
                  updateProp('items', items);
                }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium"
              >
                <Plus className="w-3.5 h-3.5" /> Add Feature
              </button>
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <input value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>CTA Text</label>
              <input value={props.cta_text || ''} onChange={(e) => updateProp('cta_text', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>CTA URL</label>
              <input value={props.cta_url || ''} onChange={(e) => updateProp('cta_url', e.target.value)}
                className={INPUT} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        );

      case 'footer':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Company Name</label>
              <input value={props.company_name || ''} onChange={(e) => updateProp('company_name', e.target.value)}
                className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Copyright Text</label>
              <input value={props.copyright_text || ''} onChange={(e) => updateProp('copyright_text', e.target.value)}
                className={INPUT} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.show_branding !== false}
                onChange={(e) => updateProp('show_branding', e.target.checked)}
                className="rounded border-stone-300" />
              Show "Powered by NurtureCRM" branding
            </label>
          </div>
        );

      case 'form':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <input value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Submit Button Text</label>
              <input value={props.submit_text || ''} onChange={(e) => updateProp('submit_text', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Success Message</label>
              <input value={props.success_message || ''} onChange={(e) => updateProp('success_message', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Form Style</label>
              <div className="flex gap-1">
                {['card', 'inline', 'minimal'].map((s) => (
                  <button key={s} onClick={() => updateProp('form_style', s)}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors capitalize ${
                      props.form_style === s ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.background_color || '#ffffff'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.background_color || '#ffffff'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.text_color || '#1c1917'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.text_color || '#1c1917'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
            </div>
            <div>
              <label className={LABEL}>Form Fields</label>
              <div className="space-y-2">
                {(props.fields || []).map((field, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Field {i + 1}</span>
                      <button onClick={() => { const fields = [...(props.fields || [])]; fields.splice(i, 1); updateProp('fields', fields); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={field.name || ''} onChange={(e) => { const fields = [...(props.fields || [])]; fields[i] = { ...fields[i], name: e.target.value }; updateProp('fields', fields); }}
                        className={INPUT} placeholder="Field name" />
                      <input value={field.label || ''} onChange={(e) => { const fields = [...(props.fields || [])]; fields[i] = { ...fields[i], label: e.target.value }; updateProp('fields', fields); }}
                        className={INPUT} placeholder="Label" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <select value={field.type || 'text'} onChange={(e) => { const fields = [...(props.fields || [])]; fields[i] = { ...fields[i], type: e.target.value }; updateProp('fields', fields); }}
                        className={INPUT}>
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="textarea">Textarea</option>
                        <option value="select">Select</option>
                        <option value="date">Date</option>
                      </select>
                      <input value={field.placeholder || ''} onChange={(e) => { const fields = [...(props.fields || [])]; fields[i] = { ...fields[i], placeholder: e.target.value }; updateProp('fields', fields); }}
                        className={INPUT} placeholder="Placeholder" />
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={field.required || false}
                        onChange={(e) => { const fields = [...(props.fields || [])]; fields[i] = { ...fields[i], required: e.target.checked }; updateProp('fields', fields); }}
                        className="rounded border-stone-300" />
                      Required
                    </label>
                  </div>
                ))}
              </div>
              <button onClick={() => { const fields = [...(props.fields || []), { name: '', label: '', type: 'text', required: false, placeholder: '' }]; updateProp('fields', fields); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Field
              </button>
            </div>
          </div>
        );

      case 'testimonials':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.show_ratings !== false}
                onChange={(e) => updateProp('show_ratings', e.target.checked)}
                className="rounded border-stone-300" />
              Show ratings
            </label>
            <div>
              <label className={LABEL}>Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className={LABEL}>Testimonials</label>
              <div className="space-y-2">
                {(props.items || []).map((item, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Testimonial {i + 1}</span>
                      <button onClick={() => { const items = [...(props.items || [])]; items.splice(i, 1); updateProp('items', items); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <input value={item.name || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], name: e.target.value }; updateProp('items', items); }}
                      className={INPUT} placeholder="Name" />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={item.role || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], role: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Role" />
                      <input value={item.company || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], company: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Company" />
                    </div>
                    <textarea value={item.quote || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], quote: e.target.value }; updateProp('items', items); }}
                      className={INPUT} rows={2} placeholder="Quote" />
                    <div>
                      <label className={LABEL}>Rating</label>
                      <select value={item.rating || 5} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], rating: parseInt(e.target.value) }; updateProp('items', items); }}
                        className={INPUT}>
                        {[1, 2, 3, 4, 5].map((r) => <option key={r} value={r}>{r} star{r > 1 ? 's' : ''}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { const items = [...(props.items || []), { name: '', role: '', company: '', quote: '', avatar_url: '', rating: 5 }]; updateProp('items', items); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Testimonial
              </button>
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <input value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.background_color || '#ffffff'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.background_color || '#ffffff'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className={LABEL}>Plans</label>
              <div className="space-y-2">
                {(props.plans || []).map((plan, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Plan {i + 1}</span>
                      <button onClick={() => { const plans = [...(props.plans || [])]; plans.splice(i, 1); updateProp('plans', plans); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <input value={plan.name || ''} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], name: e.target.value }; updateProp('plans', plans); }}
                      className={INPUT} placeholder="Plan name" />
                    <div className="grid grid-cols-2 gap-2">
                      <input value={plan.price || ''} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], price: e.target.value }; updateProp('plans', plans); }}
                        className={INPUT} placeholder="$29" />
                      <select value={plan.period || 'monthly'} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], period: e.target.value }; updateProp('plans', plans); }}
                        className={INPUT}>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                        <option value="one-time">One-time</option>
                      </select>
                    </div>
                    <input value={plan.description || ''} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], description: e.target.value }; updateProp('plans', plans); }}
                      className={INPUT} placeholder="Short description" />
                    <div>
                      <label className={LABEL}>Features (comma-separated)</label>
                      <input value={(plan.features || []).join(', ')} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], features: e.target.value.split(',').map(f => f.trim()).filter(Boolean) }; updateProp('plans', plans); }}
                        className={INPUT} placeholder="Feature 1, Feature 2, Feature 3" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={plan.cta_text || ''} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], cta_text: e.target.value }; updateProp('plans', plans); }}
                        className={INPUT} placeholder="CTA text" />
                      <input value={plan.cta_url || ''} onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], cta_url: e.target.value }; updateProp('plans', plans); }}
                        className={INPUT} placeholder="CTA URL" />
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                      <input type="checkbox" checked={plan.is_popular || false}
                        onChange={(e) => { const plans = [...(props.plans || [])]; plans[i] = { ...plans[i], is_popular: e.target.checked }; updateProp('plans', plans); }}
                        className="rounded border-stone-300" />
                      Popular plan
                    </label>
                  </div>
                ))}
              </div>
              <button onClick={() => { const plans = [...(props.plans || []), { name: '', price: '', period: 'monthly', description: '', features: [], cta_text: 'Get Started', cta_url: '#', is_popular: false, badge_text: '' }]; updateProp('plans', plans); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Plan
              </button>
            </div>
          </div>
        );

      case 'booking':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <input value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Description</label>
              <textarea value={props.description || ''} onChange={(e) => updateProp('description', e.target.value)}
                className={INPUT} rows={3} placeholder="Describe what the meeting is about..." />
            </div>
            <div>
              <label className={LABEL}>Booking URL</label>
              <input value={props.booking_url || ''} onChange={(e) => updateProp('booking_url', e.target.value)}
                className={INPUT} placeholder="https://..." />
            </div>
            <div>
              <label className={LABEL}>Button Text</label>
              <input value={props.button_text || ''} onChange={(e) => updateProp('button_text', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Duration</label>
              <input value={props.duration || ''} onChange={(e) => updateProp('duration', e.target.value)}
                className={INPUT} placeholder="30 min" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.background_color || '#ffffff'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.background_color || '#ffffff'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.text_color || '#1c1917'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.text_color || '#1c1917'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Subheadline</label>
              <input value={props.subheadline || ''} onChange={(e) => updateProp('subheadline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className={LABEL}>FAQ Items</label>
              <div className="space-y-2">
                {(props.items || []).map((item, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Question {i + 1}</span>
                      <button onClick={() => { const items = [...(props.items || [])]; items.splice(i, 1); updateProp('items', items); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <input value={item.question || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], question: e.target.value }; updateProp('items', items); }}
                      className={INPUT} placeholder="Question" />
                    <textarea value={item.answer || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], answer: e.target.value }; updateProp('items', items); }}
                      className={INPUT} rows={3} placeholder="Answer" />
                  </div>
                ))}
              </div>
              <button onClick={() => { const items = [...(props.items || []), { question: '', answer: '' }]; updateProp('items', items); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Question
              </button>
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} placeholder="Optional headline" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={LABEL}>Background</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.background_color || '#1c1917'}
                    onChange={(e) => updateProp('background_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className={LABEL}>Text Color</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                  <input value={props.text_color || '#ffffff'}
                    onChange={(e) => updateProp('text_color', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
                </div>
              </div>
            </div>
            <div>
              <label className={LABEL}>Statistics</label>
              <div className="space-y-2">
                {(props.items || []).map((item, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Stat {i + 1}</span>
                      <button onClick={() => { const items = [...(props.items || [])]; items.splice(i, 1); updateProp('items', items); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={item.value || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], value: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Value (e.g. 10K+)" />
                      <input value={item.label || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], label: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Label" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={item.prefix || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], prefix: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Prefix (e.g. $)" />
                      <input value={item.suffix || ''} onChange={(e) => { const items = [...(props.items || [])]; items[i] = { ...items[i], suffix: e.target.value }; updateProp('items', items); }}
                        className={INPUT} placeholder="Suffix (e.g. /5)" />
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => { const items = [...(props.items || []), { value: '', label: '', prefix: '', suffix: '' }]; updateProp('items', items); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Stat
              </button>
            </div>
          </div>
        );

      case 'logo_cloud':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.grayscale !== false}
                onChange={(e) => updateProp('grayscale', e.target.checked)}
                className="rounded border-stone-300" />
              Grayscale logos (color on hover)
            </label>
            <div>
              <label className={LABEL}>Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.background_color || '#fafaf5'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
            <div>
              <label className={LABEL}>Logos</label>
              <div className="space-y-2">
                {(props.logos || []).map((logo, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Logo {i + 1}</span>
                      <button onClick={() => { const logos = [...(props.logos || [])]; logos.splice(i, 1); updateProp('logos', logos); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <input value={logo.name || ''} onChange={(e) => { const logos = [...(props.logos || [])]; logos[i] = { ...logos[i], name: e.target.value }; updateProp('logos', logos); }}
                      className={INPUT} placeholder="Company name" />
                    <input value={logo.image_url || ''} onChange={(e) => { const logos = [...(props.logos || [])]; logos[i] = { ...logos[i], image_url: e.target.value }; updateProp('logos', logos); }}
                      className={INPUT} placeholder="Image URL" />
                    <input value={logo.url || ''} onChange={(e) => { const logos = [...(props.logos || [])]; logos[i] = { ...logos[i], url: e.target.value }; updateProp('logos', logos); }}
                      className={INPUT} placeholder="Link URL (optional)" />
                  </div>
                ))}
              </div>
              <button onClick={() => { const logos = [...(props.logos || []), { name: '', image_url: '', url: '' }]; updateProp('logos', logos); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Logo
              </button>
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} placeholder="Optional headline" />
            </div>
            <div>
              <label className={LABEL}>Video URL</label>
              <input value={props.video_url || ''} onChange={(e) => updateProp('video_url', e.target.value)}
                className={INPUT} placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..." />
            </div>
            <div>
              <label className={LABEL}>Thumbnail URL</label>
              <input value={props.thumbnail_url || ''} onChange={(e) => updateProp('thumbnail_url', e.target.value)}
                className={INPUT} placeholder="https://... (optional)" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.autoplay || false}
                onChange={(e) => updateProp('autoplay', e.target.checked)}
                className="rounded border-stone-300" />
              Autoplay (muted)
            </label>
            <div>
              <label className={LABEL}>Aspect Ratio</label>
              <select value={props.aspect_ratio || '16:9'} onChange={(e) => updateProp('aspect_ratio', e.target.value)}
                className={INPUT}>
                <option value="16:9">16:9 (Widescreen)</option>
                <option value="4:3">4:3 (Standard)</option>
                <option value="1:1">1:1 (Square)</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Background</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.background_color || '#ffffff'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.background_color || '#ffffff'}
                  onChange={(e) => updateProp('background_color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
          </div>
        );

      case 'divider':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Height</label>
              <select value={props.height || '40px'} onChange={(e) => updateProp('height', e.target.value)}
                className={INPUT}>
                <option value="20px">20px</option>
                <option value="40px">40px</option>
                <option value="60px">60px</option>
                <option value="80px">80px</option>
                <option value="120px">120px</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={props.color || '#d6d3d1'}
                  onChange={(e) => updateProp('color', e.target.value)}
                  className="w-8 h-8 rounded border border-stone-200 cursor-pointer" />
                <input value={props.color || '#d6d3d1'}
                  onChange={(e) => updateProp('color', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-xs border border-stone-200 rounded-lg" />
              </div>
            </div>
            {block.variant === 'line' && (
              <div>
                <label className={LABEL}>Line Style</label>
                <div className="flex gap-1">
                  {['solid', 'dashed', 'dotted'].map((s) => (
                    <button key={s} onClick={() => updateProp('style_variant', s)}
                      className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors capitalize ${
                        (props.style_variant || 'solid') === s ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                      }`}>{s}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-4">
            <div>
              <label className={LABEL}>Variant</label>
              <div className="grid grid-cols-2 gap-2">
                {(definition?.variants || []).map((v) => (
                  <button key={v} onClick={() => onUpdate(block.block_id, { ...props, __variant: v }, v)}
                    className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
                      block.variant === v ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{v.replace(/_/g, ' ')}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={LABEL}>Headline</label>
              <input value={props.headline || ''} onChange={(e) => updateProp('headline', e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className={LABEL}>Columns</label>
              <select value={props.columns || 3} onChange={(e) => updateProp('columns', parseInt(e.target.value))}
                className={INPUT}>
                <option value={2}>2 Columns</option>
                <option value={3}>3 Columns</option>
                <option value={4}>4 Columns</option>
              </select>
            </div>
            <div>
              <label className={LABEL}>Gap Size</label>
              <div className="flex gap-1">
                {['sm', 'md', 'lg'].map((g) => (
                  <button key={g} onClick={() => updateProp('gap', g)}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg border transition-colors uppercase ${
                      (props.gap || 'md') === g ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-300'
                    }`}>{g}</button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={props.rounded !== false}
                onChange={(e) => updateProp('rounded', e.target.checked)}
                className="rounded border-stone-300" />
              Rounded corners
            </label>
            <div>
              <label className={LABEL}>Images</label>
              <div className="space-y-2">
                {(props.images || []).map((img, i) => (
                  <div key={i} className="p-3 bg-stone-50 rounded-lg border border-stone-200 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold text-stone-400 uppercase">Image {i + 1}</span>
                      <button onClick={() => { const images = [...(props.images || [])]; images.splice(i, 1); updateProp('images', images); }}
                        className="text-stone-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <input value={img.image_url || ''} onChange={(e) => { const images = [...(props.images || [])]; images[i] = { ...images[i], image_url: e.target.value }; updateProp('images', images); }}
                      className={INPUT} placeholder="Image URL" />
                    <input value={img.alt_text || ''} onChange={(e) => { const images = [...(props.images || [])]; images[i] = { ...images[i], alt_text: e.target.value }; updateProp('images', images); }}
                      className={INPUT} placeholder="Alt text" />
                    <input value={img.caption || ''} onChange={(e) => { const images = [...(props.images || [])]; images[i] = { ...images[i], caption: e.target.value }; updateProp('images', images); }}
                      className={INPUT} placeholder="Caption (optional)" />
                  </div>
                ))}
              </div>
              <button onClick={() => { const images = [...(props.images || []), { image_url: '', alt_text: '', caption: '' }]; updateProp('images', images); }}
                className="mt-2 flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700 font-medium">
                <Plus className="w-3.5 h-3.5" /> Add Image
              </button>
            </div>
          </div>
        );

      default:
        return <p className="text-xs text-stone-400">No properties editor for this block type.</p>;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-stone-200 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-stone-500" />
          <span className="text-sm font-semibold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {definition?.label || block.block_type}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onDuplicate(block.block_id)}
            className="p-1.5 text-stone-400 hover:text-stone-700 rounded-md hover:bg-stone-100 transition-colors"
            title="Duplicate block">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(block.block_id)}
            className="p-1.5 text-stone-400 hover:text-red-500 rounded-md hover:bg-stone-100 transition-colors"
            title="Delete block">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Editor */}
      <div className="flex-1 overflow-y-auto p-4">
        {renderEditor()}
      </div>
    </div>
  );
}

// ─── Main SiteBuilder Component ────────────────────────────────────────────────

export default function SiteBuilder() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  // Core state
  const [site, setSite] = useState(null);
  const [pages, setPages] = useState([]);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [rawCode, setRawCode] = useState({ html: '', css: '', js: '' });   // Batch 3: raw_code pages
  const [rawRevisions, setRawRevisions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [isDraggingBlock, setIsDraggingBlock] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('desktop');
  const [loading, setLoading] = useState(true);

  // Undo/redo
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // Save state
  const [isDirty, setIsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'dirty'
  const [publishing, setPublishing] = useState(false);
  const saveTimerRef = useRef(null);

  // AI panel state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [showFigmaImport, setShowFigmaImport] = useState(false);
  const [showURLImport, setShowURLImport] = useState(false);
  const [showGlobalStyles, setShowGlobalStyles] = useState(false);

  // A/B Test & Domain panel state
  const [showABTest, setShowABTest] = useState(false);
  const [showDomainPanel, setShowDomainPanel] = useState(false);
  const [formFlowPicker, setFormFlowPicker] = useState(null);   // { pageId } when choosing a Form Flow (2b-ii)
  const [availableFlows, setAvailableFlows] = useState([]);

  // Sidebar state
  const [leftTab, setLeftTab] = useState('blocks'); // 'blocks' | 'layers'
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [editingName, setEditingName] = useState(false);
  const [siteName, setSiteName] = useState('');
  const [showPageDropdown, setShowPageDropdown] = useState(false);

  // Layer drag state
  const layerDragRef = useRef(null);

  // Current page
  const currentPage = useMemo(() => pages.find((p) => p.page_id === currentPageId), [pages, currentPageId]);
  const selectedBlock = useMemo(() => blocks.find((b) => b.block_id === selectedBlockId), [blocks, selectedBlockId]);

  // Funnel mode detection
  const isFunnel = site?.site_type === 'funnel';
  const funnelPages = useMemo(
    () => isFunnel ? [...pages].sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0)) : pages,
    [pages, isFunnel]
  );

  // ─── Load Site + Pages ─────────────────────────────────────────────────────

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const headers = getAuthHeader();
        const [siteRes, pagesRes] = await Promise.all([
          axios.get(`${API}/sites/${siteId}`, { headers }),
          axios.get(`${API}/sites/${siteId}/pages`, { headers }),
        ]);

        setSite(siteRes.data);
        setSiteName(siteRes.data.name || 'Untitled Site');

        let sitePages = Array.isArray(pagesRes.data) ? pagesRes.data : pagesRes.data?.data || [];

        // Auto-create first page if none exist
        if (sitePages.length === 0) {
          const isFunnelSite = siteRes.data?.site_type === 'funnel';
          const newPage = await axios.post(`${API}/sites/${siteId}/pages`, {
            name: isFunnelSite ? 'Step 1: Landing' : 'Home',
            slug: isFunnelSite ? '/step-1' : '/',
            page_type: isFunnelSite ? 'funnel_step' : 'page',
            funnel_order: 0,
            funnel_config: isFunnelSite ? { conversion_goal: 'form_submission' } : null,
            blocks: [],
          }, { headers });
          sitePages = [newPage.data];
        }

        // For funnels, sort by funnel_order
        if (siteRes.data?.site_type === 'funnel') {
          sitePages.sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0));
        }

        setPages(sitePages);
        const firstPage = sitePages[0];
        setCurrentPageId(firstPage.page_id);
        setBlocks(firstPage.blocks || []);
        setRawCode(firstPage.page_type === 'raw_code'
          ? { html: '', css: '', js: '', ...(firstPage.raw_code || {}) }
          : { html: '', css: '', js: '' });
      } catch (err) {
        toast.error('Failed to load site');
        navigate('/sites');
      } finally {
        setLoading(false);
      }
    };

    fetchSite();
  }, [siteId, getAuthHeader, navigate]);

  // ─── Switch Page ───────────────────────────────────────────────────────────

  const switchPage = useCallback((pageId) => {
    const page = pages.find((p) => p.page_id === pageId);
    if (!page) return;
    setCurrentPageId(pageId);
    setBlocks(page.blocks || []);
    setRawCode(page.page_type === 'raw_code'
      ? { html: '', css: '', js: '', ...(page.raw_code || {}) }
      : { html: '', css: '', js: '' });
    setRawRevisions([]);
    setSelectedBlockId(null);
    setUndoStack([]);
    setRedoStack([]);
    setIsDirty(false);
    setShowPageDropdown(false);
  }, [pages]);

  // Listen for switch-page events from ABTestPanel
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.pageId) switchPage(e.detail.pageId);
    };
    window.addEventListener('switch-page', handler);
    return () => window.removeEventListener('switch-page', handler);
  }, [switchPage]);

  // ─── Auto-save with debounce ──────────────────────────────────────────────

  const saveBlocks = useCallback(async (blocksToSave) => {
    if (!currentPageId) return;
    setSaveStatus('saving');
    try {
      await axios.put(
        `${API}/sites/${siteId}/pages/${currentPageId}/blocks`,
        { blocks: blocksToSave },
        { headers: getAuthHeader() }
      );
      setSaveStatus('saved');
      setIsDirty(false);
    } catch (err) {
      setSaveStatus('dirty');
      toast.error('Failed to save changes');
    }
  }, [siteId, currentPageId, getAuthHeader]);

  // Batch 3 — autosave raw_code (mirrors saveBlocks, hits the raw-code endpoint).
  const saveRawCode = useCallback(async (codeToSave) => {
    if (!currentPageId) return;
    setSaveStatus('saving');
    try {
      await axios.put(
        `${API}/sites/${siteId}/pages/${currentPageId}/raw-code`,
        { html: codeToSave.html || '', css: codeToSave.css || '', js: codeToSave.js || '' },
        { headers: getAuthHeader() }
      );
      setSaveStatus('saved');
      setIsDirty(false);
    } catch (err) {
      setSaveStatus('dirty');
      toast.error('Failed to save changes');
    }
  }, [siteId, currentPageId, getAuthHeader]);

  const isRawCodePage = currentPage?.page_type === 'raw_code';

  const updateRawCode = useCallback((next) => {
    setRawCode(next);
    setIsDirty(true);
  }, []);

  useEffect(() => {
    if (!isDirty || !currentPageId) return;

    setSaveStatus('dirty');

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      if (isRawCodePage) saveRawCode(rawCode);
      else saveBlocks(blocks);
    }, 2000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [blocks, rawCode, isRawCodePage, isDirty, currentPageId, saveBlocks, saveRawCode]);

  // ─── Undo / Redo helpers ──────────────────────────────────────────────────

  const pushUndo = useCallback((prevBlocks) => {
    setUndoStack((prev) => [...prev.slice(-49), JSON.parse(JSON.stringify(prevBlocks))]);
    setRedoStack([]);
  }, []);

  const updateBlocks = useCallback((newBlocks, skipUndo = false) => {
    if (!skipUndo) {
      pushUndo(blocks);
    }
    setBlocks(newBlocks);
    setIsDirty(true);
  }, [blocks, pushUndo]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((r) => [...r, JSON.parse(JSON.stringify(blocks))]);
    setUndoStack((u) => u.slice(0, -1));
    setBlocks(prev);
    setIsDirty(true);
  }, [undoStack, blocks]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((u) => [...u, JSON.parse(JSON.stringify(blocks))]);
    setRedoStack((r) => r.slice(0, -1));
    setBlocks(next);
    setIsDirty(true);
  }, [redoStack, blocks]);

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) handleRedo();
        else handleUndo();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedBlockId && !['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) {
          e.preventDefault();
          handleBlockDelete(selectedBlockId);
        }
      }
      if (e.key === 'Escape') {
        setSelectedBlockId(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo, selectedBlockId]);

  // ─── Block Operations ─────────────────────────────────────────────────────

  const handleBlockDrop = useCallback((blockType, insertIndex) => {
    try {
      const newBlock = getBlockDefaults(blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(insertIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedBlockId(newBlock.block_id);
      setIsDraggingBlock(false);
    } catch (err) {
      toast.error('Unknown block type');
    }
  }, [blocks, updateBlocks]);

  // Click-to-add: append block at end (fallback for drag issues)
  const handleClickAddBlock = useCallback((blockType) => {
    try {
      const newBlock = getBlockDefaults(blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.block_id);
    } catch (err) {
      toast.error('Unknown block type');
    }
  }, [blocks, updateBlocks]);

  const handleBlockSelect = useCallback((blockId) => {
    setSelectedBlockId(blockId);
  }, []);

  const handleBlockUpdate = useCallback((blockId, newProps, newVariant) => {
    const newBlocks = blocks.map((b) => {
      if (b.block_id !== blockId) return b;
      const updated = { ...b, props: { ...newProps } };
      if (newVariant) updated.variant = newVariant;
      // Remove internal variant key if present
      delete updated.props.__variant;
      return updated;
    });
    updateBlocks(newBlocks);
  }, [blocks, updateBlocks]);

  const handleBlockDelete = useCallback((blockId) => {
    const newBlocks = blocks.filter((b) => b.block_id !== blockId);
    updateBlocks(newBlocks);
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }, [blocks, updateBlocks, selectedBlockId]);

  const handleBlockReorder = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newBlocks = [...blocks];
    const [moved] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, moved);
    updateBlocks(newBlocks);
  }, [blocks, updateBlocks]);

  const handleBlockDuplicate = useCallback((blockId) => {
    const sourceIdx = blocks.findIndex((b) => b.block_id === blockId);
    if (sourceIdx === -1) return;
    const source = blocks[sourceIdx];
    const clone = {
      ...JSON.parse(JSON.stringify(source)),
      block_id: 'blk_' + Math.random().toString(36).slice(2, 10),
    };
    const newBlocks = [...blocks];
    newBlocks.splice(sourceIdx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedBlockId(clone.block_id);
  }, [blocks, updateBlocks]);

  // ─── Publish ──────────────────────────────────────────────────────────────

  const handlePublish = async () => {
    setPublishing(true);
    try {
      // Save current page first if dirty (raw_code vs block page)
      if (isDirty) {
        if (isRawCodePage) await saveRawCode(rawCode);
        else await saveBlocks(blocks);
      }
      const res = await axios.post(`${API}/sites/${siteId}/publish`, {}, { headers: getAuthHeader() });
      const url = res.data?.published_url;
      const count = res.data?.pages_published || 0;
      toast.success(`Published ${count} page${count !== 1 ? 's' : ''}!${url ? ` URL: ${url}` : ''}`);
      // Update site state with published_url
      if (url) setSite(prev => prev ? { ...prev, status: 'published', published_url: url } : prev);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // ─── Funnel Step Handlers ──────────────────────────────────────────────────

  const handleAddFunnelStep = async () => {
    try {
      const stepNum = pages.length + 1;
      const newPage = await axios.post(`${API}/sites/${siteId}/pages`, {
        name: `Step ${stepNum}`,
        slug: `/step-${stepNum}`,
        page_type: 'funnel_step',
        funnel_order: pages.length,
        funnel_config: { conversion_goal: 'form_submission' },
      }, { headers: getAuthHeader() });
      const created = newPage.data;
      setPages(prev => [...prev, created]);
      switchPage(created.page_id);
      toast.success('Funnel step added');
    } catch (err) {
      toast.error('Failed to add step');
    }
  };

  const handleReorderFunnelSteps = async (fromIndex, toIndex) => {
    const sorted = [...pages].sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0));
    const [moved] = sorted.splice(fromIndex, 1);
    sorted.splice(toIndex, 0, moved);

    // Optimistically update local state
    const reordered = sorted.map((p, i) => ({ ...p, funnel_order: i }));
    setPages(reordered);

    // Persist to backend
    try {
      await Promise.all(
        reordered.map((p, i) =>
          axios.put(`${API}/sites/${siteId}/pages/${p.page_id}`, { funnel_order: i }, { headers: getAuthHeader() })
        )
      );
    } catch {
      toast.error('Failed to reorder steps');
    }
  };

  const handleDeleteFunnelStep = async (pageId) => {
    if (pages.length <= 1) {
      toast.error('Cannot delete the only step');
      return;
    }
    try {
      await axios.delete(`${API}/sites/${siteId}/pages/${pageId}`, { headers: getAuthHeader() });
      const remaining = pages.filter(p => p.page_id !== pageId);
      setPages(remaining);
      if (currentPageId === pageId && remaining.length > 0) {
        const sorted = remaining.sort((a, b) => (a.funnel_order ?? 0) - (b.funnel_order ?? 0));
        switchPage(sorted[0].page_id);
      }
      toast.success('Step deleted');
    } catch {
      toast.error('Failed to delete step');
    }
  };

  const handleUpdateFunnelStep = async (pageId, updates) => {
    try {
      await axios.put(`${API}/sites/${siteId}/pages/${pageId}`, updates, { headers: getAuthHeader() });
      setPages(prev => prev.map(p => p.page_id === pageId ? { ...p, ...updates } : p));
    } catch {
      toast.error('Failed to update step');
    }
  };

  // ─── Form Flow funnel step (2b-ii): pick a published flow for this step ─────
  const handleOpenFormFlowPicker = async (pageId) => {
    setFormFlowPicker({ pageId });
    try {
      const res = await axios.get(`${API}/form-flows`, { headers: getAuthHeader() });
      setAvailableFlows((res.data || []).filter(f => f.status === 'published'));
    } catch {
      toast.error('Failed to load form flows');
    }
  };
  const chooseFormFlow = (flow) => {
    const pageId = formFlowPicker?.pageId;
    if (!pageId) return;
    const page = pages.find(p => p.page_id === pageId);
    const fc = (page && page.funnel_config) || {};
    handleUpdateFunnelStep(pageId, {
      page_type: 'form_flow', name: flow.name,
      funnel_config: { ...fc, form_flow_id: flow.flow_id },
    });
    setFormFlowPicker(null);
    toast.success('Step set to Form Flow — republish to apply');
  };

  // ─── Raw Code page (Batch 3) ────────────────────────────────────────────────
  const handleAddCodePage = async () => {
    try {
      const n = pages.length + 1;
      const slugBase = `code-page-${n}`;
      const newPage = await axios.post(`${API}/sites/${siteId}/pages`, {
        name: `Code Page ${n}`,
        slug: `/${slugBase}`,
        page_type: 'raw_code',
        raw_code: { ...STARTER_RAW_CODE },
        blocks: [],
      }, { headers: getAuthHeader() });
      const created = newPage.data;
      setPages(prev => [...prev, created]);
      switchPage(created.page_id);
      toast.success('Code page added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add code page');
    }
  };

  const loadRawRevisions = useCallback(async () => {
    if (!currentPageId) return;
    try {
      const res = await axios.get(
        `${API}/sites/${siteId}/pages/${currentPageId}/revisions`,
        { headers: getAuthHeader() }
      );
      setRawRevisions(res.data?.revisions || []);
    } catch {
      toast.error('Failed to load revisions');
      setRawRevisions([]);
    }
  }, [siteId, currentPageId, getAuthHeader]);

  const restoreRawRevision = useCallback(async (idx) => {
    if (!currentPageId) return;
    try {
      const res = await axios.post(
        `${API}/sites/${siteId}/pages/${currentPageId}/revisions/${idx}/restore`,
        {}, { headers: getAuthHeader() }
      );
      const restored = res.data?.raw_code || { html: '', css: '', js: '' };
      setRawCode({ html: '', css: '', js: '', ...restored });
      setIsDirty(false);
      // Mirror the restored code onto the cached page so a switch-away/back is consistent.
      setPages(prev => prev.map(p => p.page_id === currentPageId ? { ...p, raw_code: restored } : p));
      toast.success('Restored — Publish to make it live');
    } catch {
      toast.error('Failed to restore revision');
    }
  }, [siteId, currentPageId, getAuthHeader]);

  // ─── Site name save ───────────────────────────────────────────────────────

  const saveSiteName = async () => {
    setEditingName(false);
    if (!siteName.trim()) {
      setSiteName(site?.name || 'Untitled Site');
      return;
    }
    try {
      await axios.put(`${API}/sites/${siteId}`, { name: siteName }, { headers: getAuthHeader() });
      setSite((prev) => ({ ...prev, name: siteName }));
    } catch {
      toast.error('Failed to update site name');
    }
  };

  // ─── Layer drag handlers ──────────────────────────────────────────────────

  const handleLayerDragStart = (e, index) => {
    layerDragRef.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-layer-index', String(index));
  };

  const handleLayerDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleLayerDrop = (e, toIndex) => {
    e.preventDefault();
    const fromIndex = layerDragRef.current;
    if (fromIndex == null || fromIndex === toIndex) return;
    handleBlockReorder(fromIndex, toIndex);
    layerDragRef.current = null;
  };

  // ─── Toggle category collapse ─────────────────────────────────────────────

  const toggleCategory = (catId) => {
    setCollapsedCategories((prev) => ({ ...prev, [catId]: !prev[catId] }));
  };

  // ─── Loading State ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!site) return null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col bg-stone-50">

      {/* ═══ Top Bar ═══ */}
      <div className="h-14 bg-white border-b border-stone-200 px-4 flex items-center justify-between flex-shrink-0 z-20">
        {/* Left section */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/sites')}
            className="p-1.5 text-stone-400 hover:text-stone-700 rounded-md hover:bg-stone-100 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Site name - inline editable */}
          {editingName ? (
            <input
              autoFocus
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              onBlur={saveSiteName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveSiteName(); }}
              className="text-sm font-semibold text-stone-900 bg-transparent border-b-2 border-stone-900 outline-none px-1 py-0.5"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            />
          ) : (
            <button
              onClick={() => setEditingName(true)}
              className="text-sm font-semibold text-stone-900 hover:text-stone-600 transition-colors px-1 py-0.5"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {siteName}
            </button>
          )}

          {/* Page selector dropdown — hidden for funnels (replaced by FunnelStepBar) */}
          {!isFunnel && (
            <>
              <span className="text-stone-300">|</span>
              <div className="relative">
                <button
                  onClick={() => setShowPageDropdown(!showPageDropdown)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 bg-stone-50 hover:bg-stone-100 rounded-lg border border-stone-200 transition-colors"
                >
                  {currentPage?.name || 'Select page'}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {showPageDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowPageDropdown(false)} />
                    <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1">
                      {pages.map((page) => (
                        <button
                          key={page.page_id}
                          onClick={() => switchPage(page.page_id)}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-stone-50 flex items-center gap-2 ${
                            page.page_id === currentPageId ? 'text-stone-900 font-medium bg-stone-50' : 'text-stone-600'
                          }`}
                        >
                          {page.page_id === currentPageId && <Check className="w-3 h-3" />}
                          {page.page_type === 'raw_code' && <Code className="w-3 h-3 text-stone-400 flex-shrink-0" />}
                          <span className="truncate">{page.name}</span>
                          <span className="text-stone-400 ml-auto text-[10px]">{page.slug}</span>
                        </button>
                      ))}
                      <div className="border-t border-stone-100 my-1" />
                      <button
                        onClick={handleAddCodePage}
                        className="w-full text-left px-3 py-2 text-xs text-stone-600 hover:bg-stone-50 flex items-center gap-2"
                      >
                        <Code className="w-3 h-3" /> + New code page
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Center - Device preview toggles */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-0.5">
          {[
            { key: 'desktop', icon: Monitor, label: 'Desktop' },
            { key: 'tablet', icon: Tablet, label: 'Tablet' },
            { key: 'mobile', icon: Smartphone, label: 'Mobile' },
          ].map(({ key, icon: DevIcon, label }) => (
            <button
              key={key}
              onClick={() => setPreviewDevice(key)}
              className={`p-2 rounded-md transition-colors ${
                previewDevice === key
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-400 hover:text-stone-600'
              }`}
              title={label}
            >
              <DevIcon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Global Styles */}
          <button
            onClick={() => setShowGlobalStyles(true)}
            className="p-2 text-stone-400 hover:text-stone-700 rounded-md hover:bg-stone-100 transition-colors"
            title="Site Settings"
          >
            <Settings className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-stone-200 mx-1" />

          {/* Save status */}
          <span className={`text-[10px] font-medium mr-2 ${
            saveStatus === 'saving' ? 'text-amber-500' : saveStatus === 'dirty' ? 'text-amber-500' : 'text-green-500'
          }`}>
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving...</span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>
            )}
            {saveStatus === 'dirty' && (
              <span className="flex items-center gap-1">Unsaved changes</span>
            )}
          </span>

          <button onClick={handleUndo} disabled={undoStack.length === 0}
            className="p-2 text-stone-400 hover:text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-md hover:bg-stone-100 transition-colors"
            title="Undo (Cmd+Z)">
            <Undo2 className="w-4 h-4" />
          </button>
          <button onClick={handleRedo} disabled={redoStack.length === 0}
            className="p-2 text-stone-400 hover:text-stone-700 disabled:opacity-30 disabled:cursor-not-allowed rounded-md hover:bg-stone-100 transition-colors"
            title="Redo (Cmd+Shift+Z)">
            <Redo2 className="w-4 h-4" />
          </button>

          <div className="w-px h-6 bg-stone-200 mx-1" />

          {/* AI Builder / Figma / URL import — not applicable to raw_code pages */}
          {!isRawCodePage && (
            <Button
              variant={showAIPanel ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAIPanel(!showAIPanel)}
              className={`text-xs ${showAIPanel ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> AI Builder
            </Button>
          )}

          {/* A/B Test button — only for non-funnel sites */}
          {!isFunnel && !isRawCodePage && (
            <Button
              variant={showABTest ? "default" : "outline"}
              size="sm"
              onClick={() => { setShowABTest(!showABTest); if (!showABTest) setShowDomainPanel(false); }}
              className={`text-xs ${showABTest ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
            >
              <Zap className="w-3.5 h-3.5 mr-1" /> A/B Test
            </Button>
          )}

          {/* Custom Domain button */}
          <div className="relative">
            <Button
              variant={showDomainPanel ? "default" : "outline"}
              size="sm"
              onClick={() => { setShowDomainPanel(!showDomainPanel); if (!showDomainPanel) setShowABTest(false); }}
              className={`text-xs ${showDomainPanel ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
            >
              <Globe className="w-3.5 h-3.5 mr-1" /> Domain
            </Button>
            {showDomainPanel && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowDomainPanel(false)} />
                <div className="absolute top-full right-0 mt-2 z-50">
                  <CustomDomainPanel
                    siteId={siteId}
                    site={site}
                    getAuthHeader={getAuthHeader}
                    onSiteUpdate={async () => {
                      const res = await axios.get(`${API}/sites/${siteId}`, { headers: getAuthHeader() });
                      setSite(res.data);
                    }}
                    onClose={() => setShowDomainPanel(false)}
                  />
                </div>
              </>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Open live HTML preview (generated on the fly, no publish needed)
              if (currentPageId) {
                window.open(`${API}/sites/${siteId}/pages/${currentPageId}/preview-html`, '_blank');
              } else {
                toast.info('Select a page to preview');
              }
            }}
            className="text-xs"
          >
            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            disabled={publishing}
            className="bg-green-600 hover:bg-green-700 text-white text-xs"
          >
            {publishing ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Rocket className="w-3.5 h-3.5 mr-1" />
            )}
            Publish
          </Button>
        </div>
      </div>

      {/* ═══ Funnel Step Bar (funnel mode only) ═══ */}
      {isFunnel && (
        <FunnelStepBar
          pages={funnelPages}
          currentPageId={currentPageId}
          onSelectPage={switchPage}
          onAddStep={handleAddFunnelStep}
          onReorderSteps={handleReorderFunnelSteps}
          onDeleteStep={handleDeleteFunnelStep}
          onUpdateStep={handleUpdateFunnelStep}
          onSetFormFlow={handleOpenFormFlowPicker}
        />
      )}

      {/* Form Flow picker (funnel step → Form Flow, 2b-ii) */}
      {formFlowPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={() => setFormFlowPicker(null)}>
          <div className="bg-white rounded-xl border border-stone-200 shadow-xl w-full max-w-md max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-stone-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Choose a Form Flow</h3>
            <p className="text-xs text-stone-500 mb-3">This step redirects visitors into the selected published flow, then continues to the next funnel step.</p>
            {availableFlows.length === 0 ? (
              <div className="text-center py-6 text-sm text-stone-400">No published flows. <a href="/form-flows" target="_blank" rel="noreferrer" className="text-blue-600 underline">Create one</a>.</div>
            ) : (
              <div className="space-y-1.5">
                {availableFlows.map(f => (
                  <button key={f.flow_id} onClick={() => chooseFormFlow(f)}
                    className="w-full text-left px-3 py-2.5 rounded-lg border border-stone-200 hover:border-stone-400 hover:bg-stone-50">
                    <div className="text-sm font-medium text-stone-800">{f.name}</div>
                    <div className="text-xs text-stone-400 font-mono">/flow/{f.slug}</div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4"><Button variant="outline" size="sm" onClick={() => setFormFlowPicker(null)}>Cancel</Button></div>
          </div>
        </div>
      )}

      {/* ═══ Main 3-Panel Layout ═══ */}
      <div className="flex-1 flex overflow-hidden">

        {/* ─── Raw Code page (Batch 3): full-width editor, no palette/properties ─── */}
        {isRawCodePage ? (
          <div className="flex-1 overflow-hidden">
            <RawCodeEditor
              value={rawCode}
              onChange={updateRawCode}
              revisions={rawRevisions}
              onLoadRevisions={loadRawRevisions}
              onRestore={restoreRawRevision}
            />
          </div>
        ) : (
        <>
        {/* ─── Left Sidebar ─── */}
        <div className="w-64 bg-white border-r border-stone-200 flex flex-col flex-shrink-0">
          {/* Tabs */}
          <div className="flex border-b border-stone-200">
            {[
              { key: 'blocks', label: 'Blocks', icon: Plus },
              { key: 'layers', label: 'Layers', icon: Layers },
            ].map(({ key, label, icon: TabIcon }) => (
              <button
                key={key}
                onClick={() => setLeftTab(key)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                  leftTab === key
                    ? 'border-stone-900 text-stone-900'
                    : 'border-transparent text-stone-400 hover:text-stone-600'
                }`}
              >
                <TabIcon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-3">
            {leftTab === 'blocks' && (
              <div className="space-y-4">
                {BLOCK_CATEGORIES.map((cat) => {
                  const CatIcon = CATEGORY_ICONS[cat.id] || LayoutGrid;
                  const categoryBlocks = Object.entries(BLOCK_REGISTRY).filter(
                    ([, def]) => def.category === cat.id
                  );
                  if (categoryBlocks.length === 0) return null;
                  const isCollapsed = collapsedCategories[cat.id];

                  return (
                    <div key={cat.id}>
                      <button
                        onClick={() => toggleCategory(cat.id)}
                        className="flex items-center gap-2 w-full text-left mb-2 group"
                      >
                        <ChevronRight
                          className={`w-3 h-3 text-stone-400 transition-transform ${
                            isCollapsed ? '' : 'rotate-90'
                          }`}
                        />
                        <CatIcon className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider">
                          {cat.label}
                        </span>
                      </button>
                      {!isCollapsed && (
                        <div className="space-y-1.5 ml-1">
                          {categoryBlocks.map(([type, def]) => (
                            <BlockPaletteItem key={type} blockType={type} definition={def} onDragStateChange={setIsDraggingBlock} onClickAdd={handleClickAddBlock} />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {leftTab === 'layers' && (
              <div className="space-y-0.5">
                {blocks.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-8">No blocks yet</p>
                ) : (
                  blocks.map((block, i) => (
                    <LayerItem
                      key={block.block_id}
                      block={block}
                      index={i}
                      isSelected={block.block_id === selectedBlockId}
                      onSelect={handleBlockSelect}
                      onDelete={handleBlockDelete}
                      onDragStart={handleLayerDragStart}
                      onDragOver={handleLayerDragOver}
                      onDrop={handleLayerDrop}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* ─── Canvas ─── */}
        <div
          className="flex-1 bg-stone-100 overflow-auto"
          onClick={() => setSelectedBlockId(null)}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={(e) => {
            // Canvas-level fallback: if dropped on the canvas area (not on a specific DropZone),
            // append the block at the end
            const blockType = e.dataTransfer.getData('application/x-block-type');
            if (blockType) {
              e.preventDefault();
              handleBlockDrop(blockType, blocks.length);
            }
          }}
        >
          <div
            className="mx-auto transition-all duration-300"
            style={{
              maxWidth: DEVICE_WIDTHS[previewDevice],
              minHeight: '100%',
              ...(previewDevice !== 'desktop' ? {
                boxShadow: '0 0 0 1px rgba(0,0,0,0.05), 0 4px 24px rgba(0,0,0,0.08)',
                backgroundColor: 'white',
              } : {}),
            }}
          >
            {blocks.length === 0 ? (
              /* Empty state */
              <div
                className="flex flex-col items-center justify-center h-full min-h-[calc(100vh-3.5rem)] text-stone-400"
                onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
                onDrop={(e) => {
                  e.preventDefault();
                  const blockType = e.dataTransfer.getData('application/x-block-type');
                  if (blockType) handleBlockDrop(blockType, 0);
                }}
              >
                <div className="w-16 h-16 rounded-2xl bg-stone-200/50 flex items-center justify-center mb-4">
                  <Plus className="w-8 h-8 text-stone-300" />
                </div>
                <p className="text-sm font-medium text-stone-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Drag blocks here to start building
                </p>
                <p className="text-xs text-stone-400 mt-1">or use AI to generate your page</p>
              </div>
            ) : (
              <div className={previewDevice !== 'desktop' ? '' : 'bg-white'}>
                {/* Top drop zone */}
                <DropZone index={0} onDrop={handleBlockDrop} isDragging={isDraggingBlock} />

                {blocks.map((block, i) => (
                  <React.Fragment key={block.block_id}>
                    <BlockRenderer
                      block={block}
                      isEditing={true}
                      isSelected={block.block_id === selectedBlockId}
                      onSelect={handleBlockSelect}
                    />
                    <DropZone index={i + 1} onDrop={handleBlockDrop} isDragging={isDraggingBlock} />
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ─── Right Sidebar (Properties / Funnel Settings) ─── */}
        <div className="w-80 bg-white border-l border-stone-200 flex-shrink-0 overflow-hidden">
          {isFunnel && !selectedBlock ? (
            <FunnelSettingsPanel
              page={currentPage}
              siteId={siteId}
              allPages={funnelPages}
              onUpdate={handleUpdateFunnelStep}
            />
          ) : (
            <BlockPropertiesPanel
              block={selectedBlock}
              onUpdate={handleBlockUpdate}
              onDelete={handleBlockDelete}
              onDuplicate={handleBlockDuplicate}
            />
          )}
        </div>

        {/* ─── A/B Test Panel ─── */}
        {showABTest && !isFunnel && (
          <ABTestPanel
            siteId={siteId}
            pageId={currentPageId}
            page={currentPage}
            pages={pages}
            getAuthHeader={getAuthHeader}
            onPagesUpdate={(newPages) => setPages(newPages)}
          />
        )}
        </>
        )}
      </div>

      {/* ─── AI Builder Panel ─── */}
      <AIBuilderPanel
        isOpen={showAIPanel}
        onClose={() => setShowAIPanel(false)}
        siteId={siteId}
        pageId={currentPageId}
        blocks={blocks}
        selectedBlockId={selectedBlockId}
        onBlocksUpdate={(newBlocks) => updateBlocks(newBlocks)}
        getAuthHeader={getAuthHeader}
        onFigmaImport={() => setShowFigmaImport(true)}
        onURLImport={() => setShowURLImport(true)}
      />

      {/* ─── Figma Import Modal ─── */}
      <FigmaImportModal
        isOpen={showFigmaImport}
        onClose={() => setShowFigmaImport(false)}
        siteId={siteId}
        onBlocksReady={(newBlocks, message) => {
          updateBlocks(newBlocks);
          toast.success(message);
        }}
        getAuthHeader={getAuthHeader}
      />

      {/* ─── URL Import Modal ─── */}
      <URLImportModal
        isOpen={showURLImport}
        onClose={() => setShowURLImport(false)}
        siteId={siteId}
        onBlocksReady={(newBlocks, message, siteSettings) => {
          updateBlocks(newBlocks);
          // If the AI extracted site-level settings (fonts, colors), apply them
          if (siteSettings && site) {
            const updatedSettings = { ...(site.settings || {}) };
            if (siteSettings.primary_color) updatedSettings.primary_color = siteSettings.primary_color;
            if (siteSettings.accent_color) updatedSettings.accent_color = siteSettings.accent_color;
            if (siteSettings.background_color) updatedSettings.background_color = siteSettings.background_color;
            if (siteSettings.fonts?.[0]) updatedSettings.default_font_heading = siteSettings.fonts[0];
            if (siteSettings.fonts?.[1]) updatedSettings.default_font_body = siteSettings.fonts[1];
            // Auto-save site settings
            const headers = getAuthHeader();
            axios.put(`${API}/sites/${siteId}`, { settings: updatedSettings }, { headers }).catch(() => {});
            setSite(prev => ({ ...prev, settings: updatedSettings }));
          }
          toast.success(message);
        }}
        getAuthHeader={getAuthHeader}
      />

      {/* ─── Global Styles Panel ─── */}
      {showGlobalStyles && (
        <GlobalStylesPanel
          site={site}
          siteId={siteId}
          getAuthHeader={getAuthHeader}
          onSiteUpdate={(updatedSite, close) => {
            if (updatedSite) setSite(updatedSite);
            if (close) setShowGlobalStyles(false);
          }}
        />
      )}
    </div>
  );
}
